import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import type { PatientTreatmentPlanDetailsResponse } from '../../api/generated';
import {
  comparePlansByEndDesc,
  comparePlansByStartAsc,
  getTreatmentPlanStatus,
  type TreatmentPlanStatus
} from '../../utils/treatmentPlanStatus';
import { PatientTreatmentPlanCard } from './PatientTreatmentPlanCard';
import styles from './PatientTreatmentsList.module.css';

interface PatientTreatmentsListProps {
  treatments: PatientTreatmentPlanDetailsResponse[];
}

interface ClassifiedPlan {
  plan: PatientTreatmentPlanDetailsResponse;
  status: TreatmentPlanStatus;
}

export const PatientTreatmentsList = ({ treatments }: PatientTreatmentsListProps) => {
  const { t } = useTranslation();
  const [isPastOpen, setIsPastOpen] = useState(false);

  const { activeAndUpcoming, past } = useMemo(() => {
    const classified: ClassifiedPlan[] = treatments.map((plan) => ({
      plan,
      status: getTreatmentPlanStatus(plan)
    }));
    const active = classified
      .filter((c) => c.status !== 'past')
      .sort((a, b) => comparePlansByStartAsc(a.plan, b.plan));
    const pastList = classified
      .filter((c) => c.status === 'past')
      .sort((a, b) => comparePlansByEndDesc(a.plan, b.plan));
    return { activeAndUpcoming: active, past: pastList };
  }, [treatments]);

  if (activeAndUpcoming.length === 0 && past.length === 0) {
    return (
      <div className={styles.container}>
        <section className={styles.sectionCard}>
          <p className={styles.emptyAll}>{t('patient-treatments-empty-all')}</p>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <section className={styles.sectionCard}>
        <h3 className={styles.sectionTitle}>{t('patient-treatments-section-active')}</h3>
        {activeAndUpcoming.length === 0 ? (
          <p className={styles.emptySection}>{t('patient-treatments-empty-active')}</p>
        ) : (
          <div className={styles.list}>
            {activeAndUpcoming.map(({ plan, status }) => (
              <PatientTreatmentPlanCard key={plan.id} plan={plan} status={status} />
            ))}
          </div>
        )}
      </section>

      {past.length > 0 && (
        <section className={styles.sectionCard}>
          <button
            type="button"
            className={styles.accordionHeader}
            onClick={() => setIsPastOpen((prev) => !prev)}
            aria-expanded={isPastOpen}
          >
            <span className={styles.sectionTitle}>
              {t('patient-treatments-section-past', { count: past.length })}
            </span>
            <span
              className={`${styles.accordionChevron} ${isPastOpen ? styles.accordionChevronOpen : ''}`}
            >
              <ChevronIcon />
            </span>
          </button>
          {isPastOpen && (
            <div className={`${styles.list} ${styles.listInAccordion}`}>
              {past.map(({ plan, status }) => (
                <PatientTreatmentPlanCard key={plan.id} plan={plan} status={status} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};
